import { useEffect, useState } from 'react';
import { MainLayout } from '@/layouts/MainLayout';
import { Container, Stack, PageHeader } from '@/components/ui';
import { usePermissions } from '@/permissions/hooks';
import { UserRole } from '@/permissions/types';
import { useDailyReportsStore } from '@/store/dailyReportsStore';
import { ReportCommentsCarousel } from '@/components/dailyReports/ReportCommentsCarousel';
import { ReportCommentModal } from '@/components/dailyReports/ReportCommentModal';
import { ReportCommentSkeleton } from '@/components/dailyReports/ReportCommentSkeleton';
import { RecentFixturesPreview } from '@/components/dailyReports/RecentFixturesPreview';
import { BalticPreview } from '@/components/dailyReports/BalticPreview';
import { BunkerPricesPreview } from '@/components/dailyReports/BunkerPricesPreview';
import type { ReportComment } from '@/types/dailyReports.types';

export function DailyReportsPage() {
  const { hasPermission } = usePermissions();
  const { comments, loading, fetchComments } = useDailyReportsStore();
  const [selectedComment, setSelectedComment] = useState<ReportComment | null>(null);

  // Permission checks
  const permissions = {
    commentaries: true, // Always visible
    recentFixtures: hasPermission([UserRole.RECENT_FIXTURES]),
    baltic: hasPermission([UserRole.BALTIC]),
    bunkerPrices: hasPermission([UserRole.BUNKER_PRICES]),
  };

  // Fetch comments on mount
  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleReadAll = (comment: ReportComment) => {
    setSelectedComment(comment);
  };

  const handleCloseModal = () => {
    setSelectedComment(null);
  };

  return (
    <MainLayout>
      <Container className="pb-8">
        <Stack direction="vertical" spacing={4}>
          {/* Page Header */}
          <PageHeader title="Daily Report" />

          {/* Report Comments Carousel - Always visible */}
          {permissions.commentaries && (
            <div className="col-span-full">
              {loading ? (
                <ReportCommentSkeleton />
              ) : comments.length > 0 ? (
                <ReportCommentsCarousel comments={comments} onReadAll={handleReadAll} />
              ) : (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center dark:border-dark-700 dark:bg-dark-800">
                  <p className="text-gray-500 dark:text-gray-400">No report comments available</p>
                </div>
              )}
            </div>
          )}

          {/* Recent Fixtures Section */}
          {permissions.recentFixtures && (
            <div className="col-span-full">
              <RecentFixturesPreview />
            </div>
          )}

          {/* Baltic and Bunker Prices - Two Column Grid */}
          <div className="grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-2 lg:gap-6">
            {/* Baltic Section */}
            {permissions.baltic && (
              <div className="col-span-1">
                <BalticPreview />
              </div>
            )}

            {/* Bunker Prices Section */}
            {permissions.bunkerPrices && (
              <div className="col-span-1">
                <BunkerPricesPreview />
              </div>
            )}
          </div>
        </Stack>
      </Container>

      {/* Comment Details Modal */}
      <ReportCommentModal
        comment={selectedComment}
        isOpen={!!selectedComment}
        onClose={handleCloseModal}
      />
    </MainLayout>
  );
}
